import {IconCreditCard} from "@tabler/icons-react";
import {Form, Input, notification, Select} from "@ui";
import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {adapterBankList} from "@/adapters/bankListAdapter";
import IdentityDocumentForm from "@/components/Auth/IdentityDocumentForm";
import {PrimaryButton, SecondaryButton} from "@/components/Button";
import useCompleteProfileOnboarding from "@/hooks/useCompleteProfileOnboarding";
import useIdentityValidate from "@/hooks/useIdentityValidate";
import useNeedsProfileOnboarding from "@/hooks/useNeedsProfileOnboarding";
import usePagoMovilRegistration from "@/hooks/usePagoMovilRegistration";
import useSaveProfileOnboarding from "@/hooks/useSaveProfileOnboarding";
import {getCookie} from "@/providers/cookieStorage";
import {useAuthStore} from "@/store/useAuth.store";
import {useProfileOnboardingStore} from "@/store/useProfileOnboarding.store";
import {getApiErrorCode} from "@/util/api.error";
import {
  buildNationalID,
  isAdultFromBirthdate,
  isIdentityManualFallback,
  isIdentityUnderage,
} from "@/util/identityValidation";
import {getIdentityErrorI18nKey} from "@/util/translateIdentityError";
import BaseModal from "../../BaseModal/index";
import {PHONE_PREFIXES} from "./profile-onboarding.constants";
import styles from "./profile-onboarding-modal.module.css";

const ProfileOnboardingModal = () => {
  const {t} = useTranslation();
  const [form] = Form.useForm();
  const authStatus = useAuthStore(state => state.authStatus);
  const {needsOnboarding, isLoading} = useNeedsProfileOnboarding();
  const {executePendingAction} = usePagoMovilRegistration();
  const forceOpen = useProfileOnboardingStore(state => state.forceOpen);
  const clearForceOpen = useProfileOnboardingStore(state => state.clearForceOpen);
  const {validateIdentity, isPending: isValidatingIdentity} = useIdentityValidate();
  const {completeOnboarding, isPending: isSubmittingPm} = useCompleteProfileOnboarding();
  const {saveProfile, isPending: isSavingProfile} = useSaveProfileOnboarding();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [identityError, setIdentityError] = useState(null);
  const [identityData, setIdentityData] = useState(null);
  const [manualFallback, setManualFallback] = useState(false);
  const [selectedIdType, setSelectedIdType] = useState("V");
  const prevAuthStatus = useRef(authStatus);
  const dismissedThisSessionRef = useRef(false);

  const isIdentitySubmitting = isValidatingIdentity || isSavingProfile;
  const progressSteps = step === 1 || (selectedIdType !== "RUT" && manualFallback) ? 2 : 1;

  const resetWizard = useCallback(() => {
    setStep(0);
    setIdentityData(null);
    setIdentityError(null);
    setManualFallback(false);
    setSelectedIdType("V");
    form.resetFields();
  }, [form]);

  useEffect(() => {
    if (step !== 1) return;
    const referred = getCookie("referred");
    if (referred && !form.getFieldValue("referral_code")) {
      form.setFieldsValue({referral_code: referred});
    }
  }, [step, form]);

  useEffect(() => {
    const justLoggedIn = prevAuthStatus.current !== "authenticated" && authStatus === "authenticated";
    prevAuthStatus.current = authStatus;

    if (authStatus !== "authenticated") {
      dismissedThisSessionRef.current = false;
      setOpen(false);
      resetWizard();
      return;
    }

    if (justLoggedIn) {
      dismissedThisSessionRef.current = false;
    }

    if (!isLoading && needsOnboarding && (!dismissedThisSessionRef.current || forceOpen)) {
      setOpen(true);
      if (forceOpen) {
        clearForceOpen();
      }
    }

    if (!isLoading && !needsOnboarding) {
      dismissedThisSessionRef.current = false;
      setOpen(false);
      resetWizard();
      clearForceOpen();
    }
  }, [authStatus, clearForceOpen, forceOpen, isLoading, needsOnboarding, resetWizard]);

  const handleClose = () => {
    dismissedThisSessionRef.current = true;
    setOpen(false);
    clearForceOpen();
  };

  const finishProfileOnlyOnboarding = async payload => {
    try {
      await saveProfile(payload);

      notification.success({
        message: t("onboardingSuccessTitle"),
        description: t("onboardingSuccessProfileOnly"),
      });

      setOpen(false);
      resetWizard();
      clearForceOpen();
    } catch (err) {
      const code = getApiErrorCode(err);
      notification.error({
        message: t("onboardingErrorTitle"),
        description: code ? t(getIdentityErrorI18nKey(code)) : t("onboardingErrorMessage"),
      });
    }
  };

  const handleIdentitySubmit = async data => {
    setIdentityError(null);

    if (data.idType === "RUT") {
      const profilePayload = {
        firstName: data.firstName,
        lastName: data.lastName,
        birthDate: data.birthDate,
        nationalID: buildNationalID(data.idType, data.nationalId),
      };

      await finishProfileOnlyOnboarding(profilePayload);
      return;
    }

    if (manualFallback) {
      setIdentityData({
        idType: data.idType,
        nationalId: data.nationalId,
        token: null,
        expiresAt: null,
        birthDate: data.birthDate,
        firstName: data.firstName,
        lastName: data.lastName,
      });
      setManualFallback(false);
      setStep(1);
      return;
    }

    try {
      const response = await validateIdentity({id_type: data.idType, national_id: data.nationalId});
      const apiData = response?.data;

      if (isIdentityUnderage(apiData)) {
        setIdentityError(t("identityError_underage"));
        return;
      }

      if (isIdentityManualFallback({data: apiData})) {
        setManualFallback(true);
        return;
      }

      if (!apiData?.verification_token) {
        setManualFallback(true);
        return;
      }

      if (apiData.birth_date && !isAdultFromBirthdate(apiData.birth_date)) {
        setIdentityError(t("identityError_underage"));
        return;
      }

      setIdentityData({
        idType: data.idType,
        nationalId: data.nationalId,
        token: apiData.verification_token,
        expiresAt: apiData.expires_at,
        birthDate: apiData.birth_date,
        firstName: apiData.first_name,
        lastName: apiData.last_name,
      });
      setStep(1);
    } catch (err) {
      const code = getApiErrorCode(err);
      if (isIdentityManualFallback({errorCode: code})) {
        setManualFallback(true);
        return;
      }
      setIdentityError(t(getIdentityErrorI18nKey(code)));
    }
  };

  const handlePagoMovilSubmit = async values => {
    if (!identityData) {
      setStep(0);
      return;
    }

    if (identityData.token && new Date(identityData.expiresAt) <= new Date()) {
      setIdentityError(t("identityError_verification_expired"));
      setIdentityData(null);
      setStep(0);
      return;
    }

    const fullPhone = `${values.phone_prefix}${String(values.phone_number).trim()}`;
    const fullNationalID = `${identityData.idType}-${identityData.nationalId}`;

    const referralCode = values.referral_code?.trim().toLowerCase() ?? "";
    const referred = getCookie("referred");

    const onboardingPayload = {
      firstName: identityData.firstName,
      lastName: identityData.lastName,
      nationalID: fullNationalID,
      phoneNumber: fullPhone,
      birthDate: identityData.birthDate,
      bank_code: Number(values.bank_code),
      phone_number: Number(fullPhone),
      referral_code: referred ?? referralCode,
      preferred_bonus: values.preferred_bonus || undefined,
    };

    if (identityData.token) {
      onboardingPayload.verification_token = identityData.token;
    }

    try {
      const result = await completeOnboarding(onboardingPayload);

      notification.success({
        message: t("onboardingSuccessTitle"),
        description: t("onboardingSuccessMessage"),
      });

      if (onboardingPayload.referral_code && !result?.referralConnected) {
        notification.warning({
          message: t("onboardingReferralWarningTitle"),
          description: t("invalidOrInactiveReferralCode"),
        });
      }

      setOpen(false);
      resetWizard();
      clearForceOpen();
      await executePendingAction();
    } catch (err) {
      const code = getApiErrorCode(err);
      notification.error({
        message: t("onboardingErrorTitle"),
        description: code ? t(getIdentityErrorI18nKey(code)) : t("onboardingErrorMessage"),
      });

      if (code === "verification_expired" || code === "verification_invalid" || code === "verification_required") {
        setIdentityData(null);
        setStep(0);
      }
    }
  };

  const handleIdTypeChange = useCallback(idType => {
    setSelectedIdType(idType);
    setManualFallback(false);
    setIdentityError(null);
  }, []);

  return (
    <BaseModal
      title={t("onboardingModalTitle")}
      state={open}
      closeModal={handleClose}
      width={480}
      className={styles.modal}
      destroyOnHidden
    >
      <div className={styles.wrapper}>
        <div className={styles.steps}>
          {Array.from({length: progressSteps}, (_, index) => {
            const stepId = index === 0 ? "identity" : "pago-movil";
            let barClass = "";
            if (index < step) barClass = styles.stepBarDone;
            else if (index === step) barClass = styles.stepBarActive;

            return (
              <div
                key={stepId}
                className={`${styles.stepBar} ${barClass}`.trim()}
              />
            );
          })}
        </div>

        {step === 0 ? (
          <IdentityDocumentForm
            onSubmit={handleIdentitySubmit}
            isSubmitting={isIdentitySubmitting}
            error={identityError ?? undefined}
            submitLabel={selectedIdType === "RUT" ? t("identityFormSave") : t("onboardingContinue")}
            forceManual={manualFallback}
            manualFallbackHint={manualFallback ? t("identityManualFallbackHint") : undefined}
            onIdTypeChange={handleIdTypeChange}
          />
        ) : (
          <>
            <div className={styles.identitySummary}>
              <span className={styles.identitySummaryName}>
                {identityData?.firstName} {identityData?.lastName}
              </span>
              <span className={styles.identitySummaryCedula}>
                {identityData?.idType}-{identityData?.nationalId}
              </span>
            </div>

            <p
              style={{
                color: "var(--theme-text-soft)",
                fontSize: 13,
                margin: "0 0 12px",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <IconCreditCard size={20} />
              <span>{t("onboardingPagoMovilTitle")}</span>
            </p>
            <p style={{color: "var(--theme-text-soft)", fontSize: 13, margin: "0 0 12px"}}>
              {t("onboardingPagoMovilDescription")}
            </p>

            <Form
              form={form}
              layout="vertical"
              onFinish={handlePagoMovilSubmit}
              initialValues={{phone_prefix: "0414"}}
            >
              <Form.Item
                name="bank_code"
                label={t("mobilePaymentModalBank")}
                rules={[{required: true, message: t("mobilePaymentModalSelectBankMessage")}]}
              >
                <Select
                  className={styles.bankSelect}
                  showSearch
                  placeholder={t("mobilePaymentModalSelectBank")}
                  optionFilterProp="label"
                  options={adapterBankList()}
                />
              </Form.Item>

              <Form.Item
                label={t("mobilePaymentModalPhone")}
                required
              >
                <div className={styles.phoneRow}>
                  <Form.Item
                    name="phone_prefix"
                    noStyle
                    rules={[{required: true}]}
                  >
                    <Select
                      className={styles.phonePrefix}
                      options={PHONE_PREFIXES.map(prefix => ({value: prefix, label: prefix}))}
                    />
                  </Form.Item>
                  <div className={styles.phoneNumberWrap}>
                    <Form.Item
                      name="phone_number"
                      noStyle
                      rules={[
                        {required: true, message: t("phoneNumberRequired")},
                        {pattern: /^\d{7}$/, message: t("onboardingPhoneDigits")},
                      ]}
                    >
                      <Input
                        className={styles.phoneNumber}
                        inputMode="numeric"
                        maxLength={7}
                        placeholder="1234567"
                      />
                    </Form.Item>
                  </div>
                </div>
              </Form.Item>

              <Form.Item
                name="referral_code"
                label={t("registrationReferralCode")}
              >
                <Input placeholder={t("referralCodePlaceholder")} />
              </Form.Item>

              <Form.Item
                name="preferred_bonus"
                label={t("registrationBonusTitle")}
                className={styles.preferredBonusField}
              >
                <Select
                  allowClear
                  placeholder={t("registrationBonusTitle")}
                  style={{marginBottom: 10}}
                  options={[
                    {value: "slot", label: t("registrationFormBonusSlot")},
                    {value: "casino_live", label: t("registrationFormBonusCasino")},
                    {value: "parley", label: t("registrationFormBonusParley")},
                  ]}
                />
              </Form.Item>

              <div className={styles.footer}>
                <PrimaryButton
                  htmlType="submit"
                  className={styles.footerPrimary}
                  loading={isSubmittingPm}
                >
                  {t("onboardingConfirm")}
                </PrimaryButton>
                <div className={styles.footerSecondaryRow}>
                  <SecondaryButton
                    type="button"
                    onClick={() => {
                      const wasManual = !identityData?.token;
                      setIdentityData(null);
                      if (wasManual) {
                        setManualFallback(true);
                      }
                      setStep(0);
                    }}
                    disabled={isSubmittingPm}
                  >
                    {t("onboardingBack")}
                  </SecondaryButton>
                  <SecondaryButton
                    type="button"
                    onClick={handleClose}
                    disabled={isSubmittingPm}
                  >
                    {t("onboardingLater")}
                  </SecondaryButton>
                </div>
              </div>
            </Form>
          </>
        )}
      </div>
    </BaseModal>
  );
};

export default ProfileOnboardingModal;
